import { APP_INITIALIZER, NgModule } from "@angular/core";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { TranslateLoader, TranslateModule, TranslateService } from "@ngx-translate/core";
import {TranslateCacheService, TranslateCacheSettings } from "../../translate-cache/translate-cache.service";
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";
import { appConfig, clientConfig } from "../shared/constant/app.constant";
import { CustomTranslateLoader } from "./custom-translation.loader";
import { TranslateCacheModule } from "../../translate-cache/translate-cache.module";

@NgModule({
  imports: [
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    TranslateCacheModule.forRoot({
      cacheService: {
        provide: TranslateCacheService,
        useFactory: translateCacheFactory,
        deps: [TranslateService, TranslateCacheSettings],
      },
      cacheMechanism: "Cookie", // Set the caching mechanism to use cookies
    }),
  ],
  exports: [TranslateModule],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, TranslateCacheService, CustomTranslateLoader],
      multi: true,
    },
  ]
})
export class I18nModule {}

// Factory to determine the translation loader
export function HttpLoaderFactory(http: HttpClient) {
  if (clientConfig.MERCHANTCONFIG_IN_DB && clientConfig.MERCHANTCONFIG_IN_DB.toLowerCase().trim() === "true") {
    // Return CustomTranslateLoader if the merchant config is set in the DB
    return new CustomTranslateLoader(http);
  } else {
    // Return MultiTranslateHttpLoader for default file locations
    const defaultFiles = `${appConfig.NG_APP_ROOT}/assets/i18n/defaults/`;
    const tenantFiles = `${clientConfig.MERCHANTMEDIAURL}/tenants/${clientConfig.TENANTNAME}/i18n/`;
    const merchantFiles = `${clientConfig.MERCHANTMEDIAURL}/tenants/${clientConfig.TENANTNAME}/merchants/${clientConfig.MERCHANTNAME}/i18n/`;
    return new MultiTranslateHttpLoader(http, [
      {
        prefix: defaultFiles,
        suffix: ".json",
      },
      {
        prefix: tenantFiles,
        suffix: ".json",
      },
      {
        prefix: merchantFiles,
        suffix: ".json",
      },
    ]);
  }
}

// Cache service factory for translations
export function translateCacheFactory(
  translateService: TranslateService,
  translateCacheSettings: TranslateCacheSettings
) {
  return new TranslateCacheService(translateService, translateCacheSettings);
}

// APP_INITIALIZER factory to load translations during app initialization
export function appInitializerFactory(
  translate: TranslateService,
  translateCacheService: TranslateCacheService,
  customLoader: CustomTranslateLoader
) {
  return () => new Promise<any>((resolve: any) => {
    const supportedLang = clientConfig.LANG.list || [];
    const defaultLang = clientConfig.LANG.default || "en-GB";

    // Determine the list of languages to load
    const currentLanguages = supportedLang.length ? [...supportedLang] : [defaultLang];

    // Get the browser's preferred language or cached language
    const browserLang = translateCacheService.getCachedLanguage() || translate.getBrowserCultureLang();
    let langToUse = browserLang.match(new RegExp(supportedLang.length ? supportedLang.join("|") : defaultLang))
      ? browserLang
      : defaultLang;

    // If MERCHANTCONFIG_IN_DB is true, use CustomTranslateLoader to load translations
    if (clientConfig.MERCHANTCONFIG_IN_DB && clientConfig.MERCHANTCONFIG_IN_DB.toLowerCase().trim() === "true") {
      customLoader.getAllTranslations(currentLanguages).subscribe((translations) => {
        let supportedLanguages = [];
        for (const lang of currentLanguages) {
          const translation = translations[lang];
          if (!translation || Object.keys(translation).length === 0) {
            continue;
          }
          supportedLanguages.push(lang);
        }

        translate.addLangs(supportedLanguages);

        for (const lang of supportedLanguages) {
          translate.setTranslation(lang, translations[lang], true);
        }

        // Use the detected language
        translate.use(langToUse).subscribe(() => {
          // Initialize cache after translations are loaded
          translateCacheService.init(true);
          resolve();
        });
      });
    } else {
      translate.addLangs(currentLanguages);
      // If not using CustomTranslateLoader, fallback to loading translations via MultiTranslateHttpLoader
      translate.use(langToUse).subscribe(() => {
        translateCacheService.init();
        resolve();
      });
    }
  });
}
